import { useState } from "react";
import { useAppContextNonNull } from "@contexts/AppContext";
import { TypeaheadFn } from "@components/shared/TypeaheadFn/TypeaheadFn";
import OwButton from "@components/shared/OwButton/OwButton";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwBadge from "@components/shared/OwBadge/OwBadge";
import UnanetService from "@services/UnanetService";
import IUnanetAccount from "@models/Unanet/IUnanetAccount";
import IUnanetExpenseType from "@models/Unanet/IUnanetExpenseType";
import { IUnanetCategoryMapping } from "@models/Unanet/IUnanetAccountMappingsResponse";
import { IUnanetAccountParams } from "@models/Unanet/IUnanetQueryParams";

type Props = {
    category: IUnanetCategoryMapping;
    catalogGuid: string;
    onClose: (refresh?: boolean) => void;
};

export const UnanetGlAccountMappingEdit = (props: Props) => {
    const { userModel, supplierModel, uiContext } = useAppContextNonNull();
    const { category, catalogGuid } = props;

    const effectiveMapping = category.mapping || category.effectiveMapping;

    const [revenueAccounts, setRevenueAccounts] = useState<IUnanetAccount[]>([]);
    const [cogsAccounts, setCogsAccounts] = useState<IUnanetAccount[]>([]);
    const [inventoryAccounts, setInventoryAccounts] = useState<IUnanetAccount[]>([]);

    const [selectedRevenue, setSelectedRevenue] = useState<IUnanetAccount | undefined>(
        effectiveMapping?.revenue_account_key
            ? {
                  key: effectiveMapping.revenue_account_key,
                  code: effectiveMapping.revenue_account_code ?? "",
                  description: effectiveMapping.revenue_account_name ?? "",
                  type: "REVENUE",
              }
            : undefined
    );
    const [selectedCogs, setSelectedCogs] = useState<IUnanetAccount | undefined>(
        effectiveMapping?.cogs_account_key
            ? {
                  key: effectiveMapping.cogs_account_key,
                  code: effectiveMapping.cogs_account_code ?? "",
                  description: effectiveMapping.cogs_account_name ?? "",
                  type: "EXPENSE",
              }
            : undefined
    );
    const [selectedInventory, setSelectedInventory] = useState<IUnanetAccount | undefined>(
        effectiveMapping?.inventory_account_key
            ? {
                  key: effectiveMapping.inventory_account_key,
                  code: effectiveMapping.inventory_account_code ?? "",
                  description: effectiveMapping.inventory_account_name ?? "",
                  type: "ASSET",
              }
            : undefined
    );

    const [expenseTypes, setExpenseTypes] = useState<IUnanetExpenseType[]>([]);
    const [selectedExpenseType, setSelectedExpenseType] = useState<IUnanetExpenseType | undefined>(
        effectiveMapping?.expense_type_key
            ? {
                  key: effectiveMapping.expense_type_key,
                  name: effectiveMapping.expense_type_name ?? "",
                  code: effectiveMapping.expense_type_code ?? "",
              }
            : undefined
    );

    const [isSaving, setIsSaving] = useState(false);

    const lookupAccounts = (value: string, type: IUnanetAccountParams["type"], setFn: (accounts: IUnanetAccount[]) => void) => {
        if (!value || value.length < 1) {
            setFn([]);
            return;
        }
        UnanetService.searchAccounts({ type, code: value }, userModel, supplierModel)
            .then(setFn)
            .catch(() => setFn([]));
    };

    const lookupExpenseTypes = (value: string) => {
        if (!value || value.length < 1) {
            setExpenseTypes([]);
            return;
        }
        const lower = value.toLowerCase();
        UnanetService.getExpenseTypes(userModel, supplierModel)
            .then((types) =>
                setExpenseTypes(types.filter((t) => t.name.toLowerCase().includes(lower) || t.code.toLowerCase().includes(lower)))
            )
            .catch(() => setExpenseTypes([]));
    };

    const handleSave = async () => {
        if (!selectedRevenue || !selectedCogs || !selectedInventory) {
            uiContext.notifyFn({ message: "All three accounts must be selected" } as any);
            return;
        }

        setIsSaving(true);
        try {
            await UnanetService.upsertAccountMapping(
                category.categoryGuid,
                {
                    catalogGuid,
                    revenue_account_key: selectedRevenue.key,
                    revenue_account_code: selectedRevenue.code,
                    revenue_account_name: selectedRevenue.description,
                    cogs_account_key: selectedCogs.key,
                    cogs_account_code: selectedCogs.code,
                    cogs_account_name: selectedCogs.description,
                    inventory_account_key: selectedInventory.key,
                    inventory_account_code: selectedInventory.code,
                    inventory_account_name: selectedInventory.description,
                    expense_type_key: selectedExpenseType?.key ?? null,
                    expense_type_code: selectedExpenseType?.code ?? null,
                    expense_type_name: selectedExpenseType?.name ?? null,
                },
                userModel,
                supplierModel
            );
            props.onClose(true);
        } catch (e) {
            uiContext.notifyFn({ message: "Failed to save account mapping" } as any);
        } finally {
            setIsSaving(false);
        }
    };

    const handleClearMapping = async () => {
        setIsSaving(true);
        try {
            await UnanetService.deleteAccountMapping(category.categoryGuid, catalogGuid, userModel, supplierModel);
            props.onClose(true);
        } catch (e) {
            uiContext.notifyFn({ message: "Failed to clear account mapping" } as any);
        } finally {
            setIsSaving(false);
        }
    };

    const renderAccountField = (
        label: string,
        placeholder: string,
        type: IUnanetAccountParams["type"],
        accounts: IUnanetAccount[],
        setAccounts: (a: IUnanetAccount[]) => void,
        selected: IUnanetAccount | undefined,
        setSelected: (a: IUnanetAccount | undefined) => void
    ) => (
        <div className={"mb-3"}>
            <OwLabel title={label} />
            <TypeaheadFn
                lookupFn={(value: string) => lookupAccounts(value, type, setAccounts)}
                availableOptions={accounts}
                selectedOption={selected}
                selectedOptionFn={(item: IUnanetAccount) => setSelected(item)}
                renderFn={(item: IUnanetAccount) => (
                    <span>
                        {item.code} - {item.description}
                    </span>
                )}
                titleProp={"code"}
                subTitleProp={"description"}
                valueProp={"key"}
                placeholder={placeholder}
                usePortal={true}
                isScrollable={true}
                maxHeight={300}
                clearSelectionFn={() => setSelected(undefined)}
            />
        </div>
    );

    return (
        <div className={"p-3"}>
            <h5>
                <OwLabel title={"edit_gl_mapping"} noBottomMargin />
            </h5>

            <div className={"mb-3"}>
                <OwLabel title={"category"} />
                <div className={"fw-bold"}>{category.categoryName}</div>
            </div>

            <div className={"mb-3"}>
                <OwLabel title={"status"} />
                <div>
                    {category.hasDirectMapping ? (
                        <OwBadge title={"complete"} bgColor={"bg-success"} />
                    ) : category.inheritedFrom ? (
                        <OwBadge title={`Inherited from ${category.inheritedFrom.categoryName}`} bgColor={"bg-info"} skipTranslate={true} />
                    ) : (
                        <OwBadge title={"unconfigured"} bgColor={"bg-danger"} />
                    )}
                </div>
            </div>

            {category.inheritedFrom && !category.hasDirectMapping && (
                <div className={"alert alert-info py-2 mb-3"}>
                    <small>
                        This category inherits its mapping from <strong>{category.inheritedFrom.categoryName}</strong>. Saving here will
                        create a direct mapping that overrides the inherited one.
                    </small>
                </div>
            )}

            {renderAccountField(
                "revenue_account",
                "search_revenue_accounts",
                "REVENUE",
                revenueAccounts,
                setRevenueAccounts,
                selectedRevenue,
                setSelectedRevenue
            )}

            {renderAccountField(
                "cogs_account",
                "search_cogs_accounts",
                "EXPENSE",
                cogsAccounts,
                setCogsAccounts,
                selectedCogs,
                setSelectedCogs
            )}

            {renderAccountField(
                "inventory_account",
                "search_inventory_accounts",
                "ASSET",
                inventoryAccounts,
                setInventoryAccounts,
                selectedInventory,
                setSelectedInventory
            )}

            <div className={"mb-3"}>
                <OwLabel title={"expense_type"} />
                <TypeaheadFn
                    lookupFn={lookupExpenseTypes}
                    availableOptions={expenseTypes}
                    selectedOption={selectedExpenseType}
                    selectedOptionFn={(item: IUnanetExpenseType) => setSelectedExpenseType(item)}
                    renderFn={(item: IUnanetExpenseType) => (
                        <span>
                            {item.code} - {item.name}
                        </span>
                    )}
                    titleProp={"name"}
                    valueProp={"key"}
                    placeholder={"search_expense_types"}
                    usePortal={true}
                    isScrollable={true}
                    maxHeight={300}
                    clearSelectionFn={() => setSelectedExpenseType(undefined)}
                />
            </div>

            {category.hasDirectMapping && (
                <div className={"mb-3"}>
                    <OwButton
                        title={"clear_mapping"}
                        className={"btn btn-outline-danger btn-sm"}
                        fn={handleClearMapping}
                        disabled={isSaving}
                    />
                </div>
            )}

            <div className={"d-flex gap-2"}>
                <OwButton title={"save"} className={"btn btn-primary"} fn={handleSave} disabled={isSaving} />
                <OwButton title={"cancel"} className={"btn btn-secondary"} fn={() => props.onClose(false)} disabled={isSaving} />
            </div>
        </div>
    );
};
